import ExcelJS from "exceljs";
import JSZip from "jszip";
import {
  ExcelExportColumn,
  ExcelExportSheetConfig,
  shouldIncludeWorksheetInExport,
} from "../types";
import { getExcelExportSheetConfigMap } from "../sheetRegistry";
import {
  mapWorksheetToExportRows,
  normalizeSheetRows,
  updateFieldValue,
} from "../mapping/payloadRows";
import { removeSampleTestFromPayload } from "../mapping/sampleDataFilter";
import { enrichSpaServicesExportPayload } from "../mapping/serviceDetailRows";

const sanitizeFileName = (name: string) =>
  name.replace(/[<>:"/\\|?*]/g, "_").trim() || "Sheet";

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const styleHeaderRow = (worksheet: ExcelJS.Worksheet) => {
  worksheet.getRow(1).font = { bold: true };
  worksheet.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" },
  };
};

const populateWorksheetFromMappedRows = (
  worksheet: ExcelJS.Worksheet,
  mappedRows: Record<string, unknown>[],
  columns: ExcelExportColumn[],
  templateOnly?: boolean,
): boolean => {
  if (templateOnly) {
    worksheet.columns = columns.map((column) => ({
      header: column.header,
      key: column.header,
      width: 15,
    }));
    styleHeaderRow(worksheet);
    return true;
  }

  if (mappedRows.length === 0) {
    return false;
  }

  worksheet.columns = columns.map((column) => ({
    header: column.header,
    key: column.header,
    width: 15,
  }));

  mappedRows.forEach((row) => {
    worksheet.addRow(row);
  });

  styleHeaderRow(worksheet);
  return true;
};

const populateWorksheet = (
  worksheet: ExcelJS.Worksheet,
  sheetData: unknown,
): boolean => {
  if (Array.isArray(sheetData)) {
    if (sheetData.length === 0) return false;

    const allKeys = Array.from(
      new Set(sheetData.flatMap((obj) => Object.keys(obj))),
    ).filter((key) => key !== "id");

    const standardizedData = sheetData.map((item) =>
      allKeys.reduce(
        (acc, key) => ({ ...acc, [key]: updateFieldValue(item[key]) }),
        {} as Record<string, unknown>,
      ),
    );

    worksheet.columns = allKeys.map((key) => ({
      header: key,
      key: key,
      width: 15,
    }));

    standardizedData.forEach((row) => {
      worksheet.addRow(row);
    });
  } else if (typeof sheetData === "object" && sheetData !== null) {
    const record = sheetData as Record<string, unknown>;
    const headers = Object.keys(record).filter((key) => key !== "id");

    worksheet.columns = headers.map((key) => ({
      header: key,
      key: key,
      width: 15,
    }));

    const row = headers.reduce(
      (acc, key) => ({
        ...acc,
        [key]: record[key] ?? null,
      }),
      {} as Record<string, unknown>,
    );

    worksheet.addRow(row);
  } else {
    return false;
  }

  styleHeaderRow(worksheet);
  return true;
};

const createConfiguredWorkbook = async (
  config: ExcelExportSheetConfig,
  sheetData: unknown,
): Promise<ArrayBuffer | null> => {
  const workbook = new ExcelJS.Workbook();
  let hasWorksheet = false;

  for (const worksheetConfig of config.worksheets) {
    const mappedRows = worksheetConfig.templateOnly
      ? []
      : mapWorksheetToExportRows(sheetData, worksheetConfig);

    if (!shouldIncludeWorksheetInExport(worksheetConfig, mappedRows.length)) {
      continue;
    }

    const worksheet = workbook.addWorksheet(worksheetConfig.worksheetName);
    const headersOnly = worksheetConfig.templateOnly || mappedRows.length === 0;
    const populated = populateWorksheetFromMappedRows(
      worksheet,
      mappedRows,
      worksheetConfig.columns,
      headersOnly,
    );

    if (populated) {
      hasWorksheet = true;
    } else {
      workbook.removeWorksheet(worksheet.id);
    }
  }

  if (!hasWorksheet) {
    return null;
  }

  return workbook.xlsx.writeBuffer();
};

const buildExportTimestamp = () => {
  const now = new Date();
  return `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${now.getFullYear()}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
};

export type SheetConfigExcelResult = {
  fileName: string;
  dataUtilityImportData: {
    importData: string;
    totalRows: number;
    excelSheetName: string;
  }[];
  base64: string;
};

/** One .xlsx workbook per excelFile/excelSheet config (all tabs in the same file). */
export const generateSheetConfigExcelBase64 = async (
  config: ExcelExportSheetConfig,
  payload: Record<string, unknown>,
): Promise<SheetConfigExcelResult | null> => {
  enrichSpaServicesExportPayload(payload);
  const sheetData = payload[config.payloadKey];
  if (sheetData === undefined || sheetData === null) {
    return null;
  }

  const buffer = await createConfiguredWorkbook(config, sheetData);
  if (!buffer) {
    return null;
  }

  const dataUtilityImportData = config.worksheets.flatMap((worksheetConfig) => {
    const mappedRows = worksheetConfig.templateOnly
      ? []
      : mapWorksheetToExportRows(sheetData, worksheetConfig);

    if (!shouldIncludeWorksheetInExport(worksheetConfig, mappedRows.length)) {
      return [];
    }

    return [
      {
        excelSheetName: worksheetConfig.worksheetName,
        importData: JSON.stringify({
          [worksheetConfig.worksheetName]: mappedRows,
        }),
        totalRows: mappedRows.length,
      },
    ];
  });

  if (dataUtilityImportData.length === 0) {
    return null;
  }

  return {
    fileName: `${sanitizeFileName(config.fileName)}_${buildExportTimestamp()}.xlsx`,
    dataUtilityImportData,
    base64: arrayBufferToBase64(buffer as ArrayBuffer),
  };
};

const createFallbackWorkbook = async (
  fileName: string,
  sheetData: unknown,
): Promise<ArrayBuffer | null> => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(fileName);

  if (!populateWorksheet(worksheet, sheetData)) {
    return null;
  }

  return workbook.xlsx.writeBuffer();
};

export const createExportZipBuffer = async ({
  data,
  productId,
  sheetConfigs,
}: {
  data: Record<string, unknown[] | Record<string, unknown>>;
  productId?: number;
  sheetConfigs?: Record<string, ExcelExportSheetConfig>;
}): Promise<ArrayBuffer | null> => {
  const exportData = removeSampleTestFromPayload(
    data as Record<string, unknown>,
  ) as Record<string, unknown[] | Record<string, unknown>>;
  enrichSpaServicesExportPayload(exportData as Record<string, unknown>);
  const configMap =
    sheetConfigs ?? getExcelExportSheetConfigMap(productId ?? 0);
  const zip = new JSZip();

  for (const [payloadKey, sheetData] of Object.entries(exportData)) {
    if (!sheetData) continue;

    const config = configMap[payloadKey];
    const exportFileName = config?.fileName ?? payloadKey;

    const buffer = config
      ? await createConfiguredWorkbook(config, sheetData)
      : await createFallbackWorkbook(exportFileName, sheetData);

    if (!buffer) continue;

    zip.file(`${sanitizeFileName(exportFileName)}.xlsx`, buffer);
  }

  const fileCount = Object.keys(zip.files).length;
  if (fileCount === 0) {
    return null;
  }

  return zip.generateAsync({ type: "arraybuffer" });
};

export const createExportZipBase64 = async (options: {
  data: Record<string, unknown[] | Record<string, unknown>>;
  productId?: number;
  sheetConfigs?: Record<string, ExcelExportSheetConfig>;
}): Promise<string> => {
  const buffer = await createExportZipBuffer(options);
  return buffer ? arrayBufferToBase64(buffer) : "";
};

export const exportToExcel = async ({
  data,
  filename,
  productId,
  sheetConfigs,
}: {
  data: Record<string, unknown[] | Record<string, unknown>>;
  filename: string;
  productId?: number;
  sheetConfigs?: Record<string, ExcelExportSheetConfig>;
}) => {
  const buffer = await createExportZipBuffer({ data, productId, sheetConfigs });
  if (!buffer) return;

  const zipBlob = new Blob([buffer], { type: "application/zip" });
  const zipFilename = filename.replace(/\.xlsx?$/i, ".zip");

  const link = document.createElement("a");
  link.href = URL.createObjectURL(zipBlob);
  link.download = zipFilename;
  link.click();
  URL.revokeObjectURL(link.href);
};
